/**
 * FHIR write payloads for the supervision Document Review page.
 */
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import type { SignatureInfo } from '../../appointments/fhirNoteMapping';
import {
  applySupervisorCosignature,
  removeRequestedEditsFromQR,
  setDetailedStatus,
  setSignatureOnQR,
  PROVIDER_SIGNATURE_EXT_URL,
} from '../../appointments/fhirNoteMapping';
import {
  getChartDocumentStatus,
  isChartDocumentQuestionnaireResponse,
  setChartDocumentStatus,
} from '@/lib/chart/chartDocumentWorkflow';

import type { SupervisionQueueItemWithDocumentId } from '../types';

export function getQueueDocumentReviewId(item: SupervisionQueueItemWithDocumentId): string {
  return item.documentIdForReview ?? item.appointmentId ?? item.id;
}

/** Provider sign from document review (appointment notes, chart documents, supervision meeting notes). */
export function applyProviderSignatureForDocumentReview(
  qr: QuestionnaireResponse,
  signature: SignatureInfo,
): QuestionnaireResponse {
  let updated = setSignatureOnQR(qr, PROVIDER_SIGNATURE_EXT_URL, signature);
  updated = removeRequestedEditsFromQR(updated);

  if (isChartDocumentQuestionnaireResponse(updated)) {
    return setChartDocumentStatus(updated, 'completed');
  }

  return {
    ...updated,
    status: 'completed',
    extension: setDetailedStatus(updated.extension ?? [], 'signed'),
  };
}

/**
 * Supervisor co-sign from document review.
 * Ensures chart documents regain `chart-document-status: completed` when re-signed after Request Edits.
 */
export function applySupervisorCosignatureForDocumentReview(
  qr: QuestionnaireResponse,
  signature: SignatureInfo,
): QuestionnaireResponse {
  let updated = qr;
  if (
    isChartDocumentQuestionnaireResponse(updated)
    && getChartDocumentStatus(updated) !== 'completed'
  ) {
    updated = setChartDocumentStatus(updated, 'completed');
  }
  return applySupervisorCosignature(updated, signature);
}
